import type { CSSProperties } from 'react';

/**
 * Turns a saved print format into the CSS variables, text direction and label
 * lookup that a printed document renders with.
 *
 * Deliberately takes a STRUCTURAL type rather than importing `PrintFormat` from
 * `@kss/api`: `packages/ui` has no API dependency today and adding one to share
 * a styling helper would be the wrong trade. Any object with these fields works,
 * which also makes the resolver trivial to test.
 *
 * Every slot has a fallback, so a half-configured theme still prints legibly
 * rather than rendering black-on-black or with missing rules.
 */

export interface PrintThemeInput {
  documentTitle?: string;
  logoUrl?: string;
  logoHeightMm?: number;
  brandName?: string;
  brandTagline?: string;

  accentColor?: string;
  headingColor?: string;
  foreColor?: string;
  mutedForeColor?: string;
  borderColor?: string;
  tableHeaderBgColor?: string;
  tableHeaderForeColor?: string;
  tableStripeColor?: string;
  pageBackgroundColor?: string;

  fontFamily?: string;
  baseFontSizePt?: number;
  paperSize?: string;
  orientation?: string;
  marginMm?: number;

  defaultLanguageCode?: string;

  showPageNumbers?: boolean;
  showGeneratedAt?: boolean;
  repeatHeaderOnEachPage?: boolean;

  footerNote?: string;
  labels?: Array<{
    languageCode?: string;
    labelKey?: string;
    labelText?: string;
    isActive?: boolean;
  }>;
}

/**
 * Built-in defaults. These are the values that were hardcoded across
 * `InvoiceDocument` and `ReportShell` before the theme existed, kept so an
 * untouched tenant sees exactly what they saw before.
 */
export const PRINT_THEME_DEFAULTS = {
  accent: '#a777e3',
  heading: '#7a59c4',
  fore: '#222222',
  mutedFore: '#6b7280',
  border: '#d8caf2',
  tableHeaderBg: '#ece2fb',
  tableHeaderFore: '#7a59c4',
  tableStripe: '',
  pageBackground: '#ffffff',
  fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
  baseFontSizePt: 10,
  paperSize: 'A4',
  orientation: 'portrait',
  marginMm: 12,
} as const;

/**
 * Scripts that render right-to-left.
 *
 * Direction cannot be inferred from a language code generically, and getting it
 * wrong is very visible — an Arabic invoice with correct glyphs but a
 * left-to-right table reads as broken. Kept as an explicit list; the tenant's
 * `TenantLanguage.isRtl` flag overrides it when supplied.
 */
const RTL_LANGUAGES = new Set(['ar', 'he', 'fa', 'ur', 'ps', 'sd', 'yi', 'dv']);

export function isRtlLanguage(languageCode: string | undefined): boolean {
  if (!languageCode) return false;
  // Match on the primary subtag so "ar-AE" behaves like "ar".
  const primary = languageCode.trim().toLowerCase().split(/[-_]/)[0];
  return RTL_LANGUAGES.has(primary);
}

export interface ResolvedPrintTheme {
  /** Spread onto the document container — drives every colour via CSS vars. */
  style: CSSProperties;
  /** `dir` attribute for the container. */
  dir: 'ltr' | 'rtl';
  rtl: boolean;
  /** Resolved masthead values, already falling back to sensible blanks. */
  title: string;
  logoUrl: string;
  logoHeightMm: number;
  brandName: string;
  brandTagline: string;
  footerNote: string;
  showPageNumbers: boolean;
  showGeneratedAt: boolean;
  repeatHeaderOnEachPage: boolean;
  paperSize: string;
  orientation: string;
  marginMm: number;
  /** `@page` rule matching the format's paper size, orientation and margin. */
  pageCss: string;
  /**
   * Resolve a label: requested language → format default language → the
   * built-in string passed by the caller.
   */
  t: (key: string, builtIn: string) => string;
}

const pick = (v: string | undefined, fallback: string) =>
  v && v.trim() ? v.trim() : fallback;

/**
 * @param format   the tenant's saved print format (or null/undefined for defaults)
 * @param language language to print in; falls back to the format's default
 * @param rtlHint  the tenant's own `isRtl` flag, which overrides script detection
 */
export function resolvePrintTheme(
  format: PrintThemeInput | null | undefined,
  language?: string,
  rtlHint?: boolean,
): ResolvedPrintTheme {
  const f = format ?? {};
  const d = PRINT_THEME_DEFAULTS;

  const lang = (language || f.defaultLanguageCode || 'en').trim();
  const rtl = rtlHint ?? isRtlLanguage(lang);

  const fontSize = Number(f.baseFontSizePt) > 0 ? Number(f.baseFontSizePt) : d.baseFontSizePt;
  const paperSize = pick(f.paperSize, d.paperSize);
  const orientation = pick(f.orientation, d.orientation);
  const marginMm = Number(f.marginMm) > 0 ? Number(f.marginMm) : d.marginMm;
  const stripe = pick(f.tableStripeColor, d.tableStripe);

  const style = {
    // Colour is driven entirely by these variables, so a saved theme restyles a
    // document without any markup changing.
    '--print-accent': pick(f.accentColor, d.accent),
    '--print-heading': pick(f.headingColor, d.heading),
    '--print-fore': pick(f.foreColor, d.fore),
    '--print-muted': pick(f.mutedForeColor, d.mutedFore),
    '--print-border': pick(f.borderColor, d.border),
    '--print-thead-bg': pick(f.tableHeaderBgColor, d.tableHeaderBg),
    '--print-thead-fore': pick(f.tableHeaderForeColor, d.tableHeaderFore),
    // Empty disables striping — `transparent` keeps the CSS valid either way.
    '--print-stripe': stripe || 'transparent',
    '--print-page-bg': pick(f.pageBackgroundColor, d.pageBackground),
    fontFamily: pick(f.fontFamily, d.fontFamily),
    fontSize: `${fontSize}pt`,
    color: 'var(--print-fore)',
    background: 'var(--print-page-bg)',
  } as CSSProperties;

  const pageCss = `@page { size: ${paperSize} ${orientation}; margin: ${marginMm}mm; }`;

  // Index once so `t()` is O(1) per lookup rather than scanning the label list.
  const byKey = new Map<string, Map<string, string>>();
  for (const l of f.labels ?? []) {
    if (l.isActive === false || !l.labelKey || !l.labelText) continue;
    const k = l.labelKey.trim();
    if (!byKey.has(k)) byKey.set(k, new Map());
    byKey.get(k)!.set((l.languageCode ?? '').trim().toLowerCase(), l.labelText);
  }
  const fallbackLang = (f.defaultLanguageCode ?? 'en').trim().toLowerCase();
  const wantedLang = lang.toLowerCase();

  const t = (key: string, builtIn: string): string => {
    const forKey = byKey.get(key);
    if (!forKey) return builtIn;
    return forKey.get(wantedLang) ?? forKey.get(fallbackLang) ?? builtIn;
  };

  return {
    style,
    dir: rtl ? 'rtl' : 'ltr',
    rtl,
    title: pick(f.documentTitle, ''),
    logoUrl: pick(f.logoUrl, ''),
    logoHeightMm: Number(f.logoHeightMm) > 0 ? Number(f.logoHeightMm) : 0,
    brandName: pick(f.brandName, ''),
    brandTagline: pick(f.brandTagline, ''),
    footerNote: pick(f.footerNote, ''),
    showPageNumbers: f.showPageNumbers !== false,
    showGeneratedAt: f.showGeneratedAt !== false,
    repeatHeaderOnEachPage: f.repeatHeaderOnEachPage !== false,
    paperSize,
    orientation,
    marginMm,
    pageCss,
    t,
  };
}

/* ------------------------------------------------------------------ */
/* Value formatting                                                    */
/* ------------------------------------------------------------------ */

export type PrintValueFormat =
  | 'text'
  | 'number'
  | 'currency'
  | 'percent'
  | 'date'
  | 'datetime';

/**
 * Format a cell for print.
 *
 * Locale-aware so a translated document also gets the right digit grouping —
 * printing "1,234.50" in an Arabic document while every label is Arabic is the
 * kind of half-translation that looks worse than not translating at all.
 *
 * An unparseable value is returned as-is rather than becoming "NaN": a report
 * cell that shows the raw text is recoverable, one that shows NaN is not.
 */
export function formatPrintValue(
  value: unknown,
  format: PrintValueFormat | undefined,
  opts: { locale?: string; currency?: string; decimals?: number } = {},
): string {
  if (value == null || value === '') return '';

  const locale = opts.locale || 'en-IN';
  const currency = opts.currency || 'INR';

  switch (format) {
    case 'number':
    case 'currency':
    case 'percent': {
      const n = typeof value === 'number' ? value : Number(String(value).replace(/[, ]/g, ''));
      if (!Number.isFinite(n)) return String(value);
      const decimals = opts.decimals;
      if (format === 'currency') {
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
          minimumFractionDigits: decimals ?? 2,
          maximumFractionDigits: decimals ?? 2,
        }).format(n);
      }
      if (format === 'percent') {
        return `${new Intl.NumberFormat(locale, {
          minimumFractionDigits: decimals ?? 2,
          maximumFractionDigits: decimals ?? 2,
        }).format(n)}%`;
      }
      return new Intl.NumberFormat(locale, {
        minimumFractionDigits: decimals ?? 0,
        maximumFractionDigits: decimals ?? 2,
      }).format(n);
    }
    case 'date':
    case 'datetime': {
      const dt = value instanceof Date ? value : new Date(String(value));
      if (Number.isNaN(dt.getTime())) return String(value);
      return format === 'date'
        ? dt.toLocaleDateString(locale)
        : dt.toLocaleString(locale);
    }
    default:
      return String(value);
  }
}

/** Default alignment when the column doesn't specify one. */
export function defaultAlign(format: PrintValueFormat | undefined): 'left' | 'right' {
  return format === 'number' || format === 'currency' || format === 'percent' ? 'right' : 'left';
}

/** Apply a footer aggregate across a set of row values. */
export function aggregate(
  fn: 'sum' | 'avg' | 'min' | 'max' | 'count' | undefined,
  values: unknown[],
): number | null {
  if (!fn) return null;
  if (fn === 'count') return values.length;

  /* Blanks must be excluded BEFORE coercion. `Number('')` and `Number(null)` are
     both 0, so a column with no data would otherwise total to a confident
     "0.00" — which reads as "zero revenue" rather than "nothing to total". */
  const nums = values
    .filter((v) => v !== null && v !== undefined && String(v).trim() !== '')
    .map((v) => (typeof v === 'number' ? v : Number(String(v).replace(/[, ]/g, ''))))
    .filter((n) => Number.isFinite(n));
  if (nums.length === 0) return null;

  switch (fn) {
    case 'sum': return nums.reduce((a, b) => a + b, 0);
    case 'avg': return nums.reduce((a, b) => a + b, 0) / nums.length;
    case 'min': return Math.min(...nums);
    case 'max': return Math.max(...nums);
    default: return null;
  }
}
